import { Component, Inject, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CompanySearchService } from '../../../company-search/services/company-search.service';
import { ICompany } from '../../../../core/models/company.model';
import { NgxPaginationModule } from 'ngx-pagination';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MonitoringService } from '../../monitoring.service';
import { IWatchlistCompany } from '../../monitoring.model';

@Component({
  selector: 'app-company-search-popup',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    NgxPaginationModule,
    ClipboardModule,
    MatTooltipModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './company-search-popup.component.html',
  styleUrl: './company-search-popup.component.css'
})
export class CompanySearchPopupComponent {

  showSpinner = false;
  companies: ICompany[] = [];
  paginator_page = 1;
  successMessage = '';
  errorMessage = '';
  mode: 'add' | 'edit' = 'add';
  editMatch: any = null;
  //if organisation is QuantIP set the limit to 500 else 200
  maxCompanies = localStorage.getItem('organisation') === 'QuantIP' ? 500 : 200;

  currentCount = 0;
  remainingSlots = 0;

  companySearchForm = new FormGroup({
    name: new FormControl(),
    isin: new FormControl(),
    url: new FormControl("", [
      Validators.pattern(
        "^(https?:\/\/)?(www\.)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$"
      ),
    ]),
    companytype: new FormControl('Public Company'),
  });

  constructor(
    private companyService: CompanySearchService,
    private monitoringService: MonitoringService,
    public dialogRef: MatDialogRef<CompanySearchPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.mode = data.mode || 'add';
    this.editMatch = data.editMatch || null;
    this.successMessage = '';
    this.errorMessage = '';
    const isBenchmark = data.isBenchmark || false;
    
    if (this.mode === 'add') {
      if (isBenchmark && data.benchmark) {
        // Benchmarks have unlimited slots
        this.currentCount = data.benchmark?.company_count || 0;
        this.remainingSlots = Infinity;
        this.maxCompanies = Infinity;
      } else if (data.watchlist) {
        this.getWatchlistCompanies();
        this.currentCount = data.watchlist?.company_count || 0;
        this.remainingSlots = this.maxCompanies - this.currentCount;
      }
    } else if (this.mode === 'edit' && this.editMatch) {
      // Pre-fill form with current match data
      this.companySearchForm.patchValue({
        name: this.editMatch.input_data?.name || '',
        isin: this.editMatch.input_data?.isin || ''
      });
      // Only load companies if it's a watchlist (benchmarks don't need this check for edit mode)
      if (!isBenchmark && data.watchlist) {
        this.getWatchlistCompanies();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['data'] && this.data.watchlist?.watchlist_id) {
      this.getWatchlistCompanies();
    }
  }
  watchlistCompanies: IWatchlistCompany[] = [];
  // Check if company already exists in watchlist (disables add)
 getWatchlistCompanies() {
    if (!this.data.watchlist?.watchlist_id) {
      return;
    }
    this.monitoringService.getWatchlistCompanies(this.data.watchlist.watchlist_id).subscribe({
      next: (res) => {
        this.watchlistCompanies = res.body?.payload || [];
        if (res.status === 204) {
          this.watchlistCompanies = [];
        }
      },
      error: () => {
        this.watchlistCompanies = [];
      }
    });
  }

  isCompanyInWatchlist(company: ICompany): boolean {
    // For edit mode with benchmarks, we don't check existing companies
    if (this.mode === 'edit' && this.data.isBenchmark) {
      return false;
    }
    return this.watchlistCompanies.some(c => c.entityid === company.entityid);
  }

  companySearch() {
    this.companies = [];
    this.showSpinner = true;
    this.paginator_page = 1;
    this.successMessage = '';
    this.errorMessage = '';
    this.companyService.companySearch(
      this.companySearchForm.value.name,
      this.companySearchForm.value.isin,
      this.companySearchForm.value.url,
      'Public Company' // default company type
    ).subscribe({
      next: (res) => {
        if (res.payload.length > 0) {
          this.companies = res.payload;
          this.showSpinner = false;
        } else {
          this.errorMessage = 'No Public Companies found';
          this.showSpinner = false;
        }
      },
      error: () => {
        this.errorMessage = 'Search Failed! No Public Companies founds';
        this.showSpinner = false;
      }
    });
  }

  
  spinnerForAdd:boolean = false;
  /** Add/edit selected company depending on mode; enforces limit and duplicates */
  addCompanyToWatchlist(company: ICompany) {
    this.successMessage = '';
    this.errorMessage = '';
    this.spinnerForAdd = false;
    if (this.mode === 'edit') {
      // Return the selected company for edit mode
      this.dialogRef.close({
        selectedCompany: company,
        originalMatch: this.editMatch
      });
    } else {
      // Check limit for add mode
      if (this.remainingSlots <= 0) {
        this.errorMessage = 'Watchlist is full. Cannot add more companies.';
        return;
      }
      this.spinnerForAdd = true;
      
      // Add to watchlist for add mode (only for watchlists, not benchmarks)
      if (!this.data.watchlist?.watchlist_id) {
        this.errorMessage = 'Invalid watchlist';
        this.spinnerForAdd = false;
        return;
      }
      this.monitoringService.addCompanyToWatchlist(this.data.watchlist.watchlist_id, company.entityid, company.entity_name, company.gsubind_extended || company.gsubind, company.isin).subscribe({
        next: () => {
          this.currentCount++;
          this.remainingSlots = this.maxCompanies - this.currentCount;
          this.getWatchlistCompanies();
          this.successMessage = 'Company added to watchlist successfully!';
          this.spinnerForAdd = false;
        },
        error: () => {
          this.errorMessage = 'Error adding company to watchlist';
          this.getWatchlistCompanies();
          this.spinnerForAdd = false;
        }
      });
    }
  }

  closePopup() {
    this.dialogRef.close();
  }
}
